import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDataService } from '@/lib/service-context';
import { useResilientMutation } from '@/lib/use-resilient-mutation';
import { listCacheFallback } from '@/lib/list-cache-fallback';
import type {
  CheckDraft,
  CheckDraftFilters,
  CheckDraftState,
  CreateCheckDraftInput,
  TransitionStateOptions,
  UpdateCheckDraftInput,
} from '@erp/data-access/interfaces';

const CHECKS_KEY = ['checks-drafts'] as const;
const EVENTS_KEY = ['check-draft-events'] as const;
const OFFLINE_PREFIX = 'OFFLINE-';

/**
 * Always fetches the full draft list and filters in memory so check
 * page tabs (chèques/traites, by direction/state) all hit the prewarmed
 * cache offline.
 */
export function useChecksDrafts(filters?: CheckDraftFilters) {
  const ds = useDataService();
  const query = useQuery({
    queryKey: [...CHECKS_KEY, undefined],
    queryFn: () => ds.checks.list(),
  });
  const filtered = useMemo(() => {
    if (!query.data) return query.data;
    if (!filters) return query.data;
    const { type, direction, currentState, thirdPartyType, thirdPartyId, bankAccountId, dueDateFrom, dueDateTo, dueBefore, dueAfter } = filters;
    return query.data.filter((d) => {
      if (type && d.type !== type) return false;
      if (direction && d.direction !== direction) return false;
      if (currentState && d.currentState !== currentState) return false;
      if (thirdPartyType && d.thirdPartyType !== thirdPartyType) return false;
      if (thirdPartyId && d.thirdPartyId !== thirdPartyId) return false;
      if (bankAccountId && d.bankAccountId !== bankAccountId) return false;
      if (dueDateFrom && d.dueDate < dueDateFrom) return false;
      if (dueDateTo && d.dueDate > dueDateTo) return false;
      if (dueBefore && d.dueDate >= dueBefore) return false;
      if (dueAfter && d.dueDate <= dueAfter) return false;
      return true;
    });
  }, [query.data, filters]);
  return { ...query, data: filtered } as typeof query;
}

export function useCheckDraft(id: string) {
  const ds = useDataService();
  const qc = useQueryClient();
  return useQuery({
    queryKey: [...CHECKS_KEY, id],
    queryFn: () => ds.checks.getById(id),
    enabled: !!id,
    placeholderData: () => listCacheFallback<CheckDraft>(qc, CHECKS_KEY, id),
  });
}

export function useCheckDraftEvents(checkDraftId: string) {
  const ds = useDataService();
  return useQuery({
    queryKey: [...EVENTS_KEY, checkDraftId],
    queryFn: () => ds.checks.listEvents(checkDraftId),
    enabled: !!checkDraftId,
  });
}

export function useChecksDueSoon() {
  const ds = useDataService();
  return useQuery({
    queryKey: [...CHECKS_KEY, 'due-soon'],
    queryFn: () => ds.checks.getDueSoon(),
    staleTime: 60 * 1000,
  });
}

export function useCreateCheckDraft() {
  const ds = useDataService();
  return useResilientMutation<CreateCheckDraftInput, CheckDraft>({
    entity: 'checks',
    op: 'create',
    invalidate: [CHECKS_KEY],
    online: (input) => ds.checks.create(input),
    optimistic: (input, clientId) =>
      ({
        id: clientId,
        type: input.type,
        direction: input.direction,
        number: input.number ?? OFFLINE_PREFIX + clientId.slice(0, 6).toUpperCase(),
        bankName: input.bankName ?? '',
      }) as CheckDraft,
    applyOptimistic: (qc, draft) => {
      qc.setQueriesData<CheckDraft[]>({ queryKey: CHECKS_KEY }, (old) =>
        Array.isArray(old) && !old.some((c) => c.id === draft.id) ? [draft, ...old] : old,
      );
    },
  });
}

export function useUpdateCheckDraft() {
  const ds = useDataService();
  return useResilientMutation<{ id: string; input: UpdateCheckDraftInput }, CheckDraft>({
    entity: 'checks',
    op: 'update',
    invalidate: [CHECKS_KEY],
    online: ({ id, input }) => ds.checks.update(id, input),
    optimistic: ({ id, input }) =>
      ({
        id,
        number: input.number ?? OFFLINE_PREFIX,
        bankName: input.bankName ?? '',
      }) as unknown as CheckDraft,
    applyOptimistic: (qc, _r, { id, input }) => {
      qc.setQueriesData<CheckDraft[]>({ queryKey: CHECKS_KEY }, (old) =>
        Array.isArray(old) ? old.map((c) => (c.id === id ? { ...c, ...input } : c)) : old,
      );
    },
  });
}

export function useDeleteCheckDraft() {
  const ds = useDataService();
  return useResilientMutation<string, void>({
    entity: 'checks',
    op: 'delete',
    invalidate: [CHECKS_KEY],
    online: (id) => ds.checks.delete(id),
    optimistic: () => undefined as unknown as void,
    applyOptimistic: (qc, _r, id) => {
      qc.setQueriesData<CheckDraft[]>({ queryKey: CHECKS_KEY }, (old) =>
        Array.isArray(old) ? old.filter((c) => c.id !== id) : old,
      );
    },
  });
}

/** @deprecated prefer useTransitionCheckDraftState */
export function useUpdateCheckDraftState() {
  const ds = useDataService();
  return useResilientMutation<{ id: string; newState: string }, CheckDraft>({
    entity: 'checks',
    op: 'custom',
    method: 'updateState',
    invalidate: [CHECKS_KEY],
    online: ({ id, newState }) => ds.checks.updateState(id, newState),
    optimistic: ({ id, newState }) => ({ id, state: newState } as unknown as CheckDraft),
  });
}

export function useTransitionCheckDraftState() {
  const ds = useDataService();
  return useResilientMutation<
    { id: string; toState: CheckDraftState; options?: TransitionStateOptions },
    CheckDraft
  >({
    entity: 'checks',
    op: 'custom',
    method: 'transitionState',
    invalidate: [CHECKS_KEY, EVENTS_KEY, ['bank-accounts'], ['bank-transactions']],
    online: ({ id, toState, options }) => ds.checks.transitionState(id, toState, options),
    optimistic: ({ id, toState }) => ({ id, state: toState } as unknown as CheckDraft),
  });
}

/**
 * Backfill bank_transactions for cashed/debited drafts that have a bank
 * account but no matching transaction row. Server-side aggregation —
 * MUST be online so the result reflects current state. Stays a regular
 * useMutation so it errors visibly when offline.
 */
import { useMutation } from '@tanstack/react-query';
export function useBackfillCheckDraftBankTransactions() {
  const ds = useDataService();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => ds.checks.backfillBankTransactions(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bank-accounts'] });
      qc.invalidateQueries({ queryKey: ['bank-transactions'] });
    },
  });
}
